import { COLORS, FONTS, fontsHref } from './tokens';
import { escapeHtml } from './escape';

export interface DocumentArgs {
  // НЕ экранирован вызывающим (в отличие от title/printButtonLabel ниже) —
  // экранируется здесь, при подстановке в атрибут. Источник — i18n.language,
  // вектора сегодня нет, но контракт должен быть явным для каждого поля, а не
  // молчаливым исключением из правила «весь подставляемый текст экранируется».
  lang: string;
  title: string; // уже экранирован вызывающим
  header: string;
  body: string;
  footer: string;
  printButtonLabel: string; // уже экранирован вызывающим
}

export function renderDocument(a: DocumentArgs): string {
  return `<!DOCTYPE html>
<html lang="${escapeHtml(a.lang)}">
<head>
<meta charset="utf-8">
<title>${a.title}</title>
<link href="${fontsHref()}" rel="stylesheet">
<style>${styles()}</style>
</head>
<body>
<button class="print-btn no-print" onclick="window.print()">${a.printButtonLabel}</button>
<div class="page">${a.header}${a.body}${a.footer}</div>
</body>
</html>`;
}

function styles(): string {
  return `:root{--ax:${COLORS.accent};--excellent:${COLORS.excellent};--good:${COLORS.good};--fair:${COLORS.fair};--poor:${COLORS.poor};--paper:${COLORS.paper};--card:${COLORS.card};--line:${COLORS.line};--line-soft:${COLORS.lineSoft};--fg:${COLORS.fg};--fg2:${COLORS.fgMuted};--fg3:${COLORS.fgSubtle};--display:${FONTS.display};--ui:${FONTS.ui};--mono:${FONTS.mono}}
${RULES}`;
}

/**
 * Правила разметки — перенос блока <style> из
 * docs/plans/2026-08-06-pdf-report-preview.html (согласованная вёрстка,
 * `*{margin:0…}` … `@media print{…}`), переформатирован читаемо. Литералы
 * заменены на var(--…) там, где переменная СЕМАНТИЧЕСКИ соответствует
 * литералу (например #efece3 → var(--line-soft) — то же назначение, тот же
 * цвет), а не просто совпадает по значению. `#fff` в `.print-btn` (текст на
 * бирюзовой кнопке) и в `@media print body` (белая бумага при печати)
 * оставлены литералами: они случайно совпадают со значением `--card`
 * («поверхность карточки»), но не связаны с ним по смыслу — токен должен
 * связывать вещи, обязанные меняться вместе, а эти не обязаны. Прочие
 * литералы без соответствующей переменной — трек прогресс-бара (#ece9df),
 * rgba-тинты бренд-цветов (нет отдельных tint-переменных в :root) и
 * разделители #f4f1e8/#f0ede4 — оставлены как в прототипе.
 */
const RULES = `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
body {
  font-family: var(--ui);
  font-size: 12px;
  line-height: 1.5;
  color: var(--fg);
  background: var(--paper);
  padding: 32px 20px;
}
.page {
  max-width: 760px;
  margin: 0 auto;
}

/* ---------- header ---------- */
.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}
.brow {
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: var(--ax);
}
.head-meta {
  text-align: right;
  font-family: var(--mono);
  font-size: 10px;
  color: var(--fg3);
  line-height: 1.7;
}
h1 {
  font-family: var(--display);
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -.01em;
  margin-top: 3px;
}

/* ---------- hero ---------- */
.hero {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 20px 22px;
  margin-bottom: 8px;
}
.hero-label {
  font-family: var(--mono);
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--fg3);
}
.hero-score {
  font-family: var(--display);
  font-size: 46px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -.02em;
  margin-top: 6px;
}
.hero-score small {
  font-family: var(--mono);
  font-size: 14px;
  font-weight: 400;
  color: var(--fg3);
  margin-left: 2px;
}
.bar {
  height: 7px;
  border-radius: 99px;
  background: #ece9df;
  overflow: hidden;
  margin-top: 14px;
}
.bar > i {
  display: block;
  height: 100%;
  border-radius: 99px;
}
.flag {
  margin-top: 12px;
  border-radius: 6px;
  background: rgba(217, 119, 6, .1);
  padding: 9px 12px;
}
.flag b {
  color: var(--fair);
  font-size: 11px;
}
.flag p {
  color: var(--fg2);
  font-size: 11px;
  margin-top: 2px;
}

/* ---------- provenance ---------- */
.prov {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--fg3);
  padding: 2px 2px 0;
  margin-bottom: 4px;
}
.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
  padding: 0 2px;
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--line);
  background: var(--card);
  border-radius: 99px;
  padding: 3px 10px;
  font-size: 10.5px;
  color: var(--fg2);
}
.badge.up {
  border-color: rgba(22, 163, 74, .35);
  color: var(--excellent);
  background: rgba(22, 163, 74, .07);
}
.badge.down {
  border-color: rgba(220, 38, 38, .35);
  color: var(--poor);
  background: rgba(220, 38, 38, .07);
}

/* ---------- group divider ---------- */
.grp {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 9px;
  padding: 0 2px;
}
.grp span {
  font-family: var(--mono);
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: var(--ax);
}
.grp i {
  flex: 1;
  height: 1px;
  background: var(--line);
}

/* ---------- section card ---------- */
.card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 8px;
  margin-bottom: 8px;
  page-break-inside: avoid;
  break-inside: avoid;
}
.card-h {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--line-soft);
}
.card-h em {
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: var(--ax);
  flex: none;
}
.card-h h2 {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: .09em;
  text-transform: uppercase;
  color: var(--fg2);
  font-style: normal;
  font-family: var(--ui);
}
.card-h .val {
  margin-left: auto;
  font-family: var(--mono);
  font-size: 12px;
  font-weight: 600;
}
.card-b {
  padding: 13px 16px;
}

/* ---------- primitives ---------- */
.metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
}
.metric {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line-soft);
  border-radius: 6px;
  padding: 7px 9px;
}
.metric .code {
  width: 19px;
  height: 19px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--mono);
  font-size: 9.5px;
  font-weight: 600;
  flex: none;
}
.metric .nm {
  flex: 1;
  font-size: 10.5px;
  color: var(--fg2);
  line-height: 1.25;
}
.metric .sc {
  font-family: var(--mono);
  font-size: 12px;
  font-weight: 600;
}
.breakdown {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 7px;
  margin-top: 9px;
}
.bd {
  text-align: center;
  border: 1px solid var(--line-soft);
  border-radius: 6px;
  padding: 8px 4px;
}
.bd .l {
  font-size: 9px;
  color: var(--fg3);
  line-height: 1.25;
  min-height: 22px;
}
.bd .v {
  font-family: var(--mono);
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
}
.kv {
  display: flex;
  gap: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #f4f1e8;
  font-size: 11.5px;
}
.kv:last-child {
  border-bottom: 0;
}
.kv dt {
  color: var(--fg3);
  flex: none;
  min-width: 150px;
}
.kv dd {
  color: var(--fg);
  font-weight: 500;
}
.lbl {
  font-family: var(--mono);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: var(--fg3);
  margin-bottom: 4px;
}
.blk {
  margin-bottom: 11px;
}
.blk:last-child {
  margin-bottom: 0;
}
.blk p {
  font-size: 11.5px;
  color: var(--fg2);
}
.tag {
  display: inline-flex;
  border-radius: 4px;
  padding: 1.5px 7px;
  font-size: 9.5px;
  font-weight: 600;
  font-family: var(--mono);
  letter-spacing: .04em;
}
.t-ok {
  background: rgba(22, 163, 74, .1);
  color: var(--excellent);
}
.t-warn {
  background: rgba(217, 119, 6, .1);
  color: var(--fair);
}
.t-bad {
  background: rgba(220, 38, 38, .1);
  color: var(--poor);
}
.t-mut {
  background: #f0ede4;
  color: var(--fg3);
}
.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 7px;
}
.box {
  border: 1px solid var(--line-soft);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
}
.box .top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}
.box .top b {
  font-size: 11px;
}
.box q {
  color: var(--fg2);
  font-style: italic;
  quotes: '«' '»';
}
ul {
  list-style: none;
}
li {
  font-size: 11.5px;
  padding: 3px 0 3px 15px;
  position: relative;
  color: var(--fg2);
}
li::before {
  content: '';
  position: absolute;
  left: 3px;
  top: 9.5px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--ax);
}
.moment {
  border-left: 3px solid var(--line);
  padding: 5px 0 5px 10px;
  margin-bottom: 6px;
}
.moment.crit {
  border-left-color: var(--poor);
}
.moment.high {
  border-left-color: var(--fair);
}
.moment .m1 {
  display: flex;
  align-items: center;
  gap: 7px;
}
.moment .tc {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--fg3);
}
.moment .tx {
  font-size: 11.5px;
  font-weight: 500;
}
.moment .q {
  font-size: 11px;
  color: var(--fg2);
  font-style: italic;
  margin-top: 1px;
}
.crt {
  border-left: 3px solid var(--line);
  padding: 5px 0 5px 10px;
  margin-bottom: 6px;
}
.crt .c1 {
  display: flex;
  align-items: center;
  gap: 7px;
}
.crt .n {
  font-size: 11.5px;
  font-weight: 500;
  flex: 1;
}
.crt .note {
  font-size: 11px;
  color: var(--fg2);
}
.crt .q {
  font-size: 11px;
  color: var(--fg2);
  font-style: italic;
  margin-top: 1px;
}
.rec {
  border-left: 3px solid var(--fair);
  background: rgba(217, 119, 6, .05);
  border-radius: 0 6px 6px 0;
  padding: 8px 11px;
  margin-bottom: 7px;
}
.rec .r1 {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 3px;
}
.rec b {
  font-size: 11.5px;
}
.rec p {
  font-size: 11px;
  color: var(--fg2);
}
.chk {
  display: flex;
  gap: 9px;
  align-items: baseline;
  padding: 5px 0;
  border-bottom: 1px solid #f4f1e8;
  font-size: 11.5px;
}
.chk:last-child {
  border-bottom: 0;
}
.chk .i {
  font-family: var(--mono);
  font-weight: 700;
  width: 12px;
  flex: none;
}
.chk .i.ok {
  color: var(--excellent);
}
.chk .i.no {
  color: var(--poor);
}
.chk .i.na {
  color: var(--fg3);
}
.chk .d {
  color: var(--fg3);
  margin-left: auto;
  font-family: var(--mono);
  font-size: 10px;
}
.note {
  background: #f0ede4;
  border-radius: 6px;
  padding: 11px 13px;
  font-size: 11.5px;
  color: var(--fg2);
  margin-bottom: 8px;
}
.note b {
  display: block;
  color: var(--fg);
  margin-bottom: 2px;
  font-size: 11.5px;
}

/* ---------- footer ---------- */
.foot {
  margin-top: 26px;
  padding-top: 11px;
  border-top: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  font-family: var(--mono);
  font-size: 9.5px;
  color: var(--fg3);
}

.print-btn {
  position: fixed;
  bottom: 22px;
  right: 22px;
  padding: 11px 20px;
  background: var(--ax);
  color: #fff;
  border: 0;
  border-radius: 8px;
  font-family: var(--ui);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, .16);
}

@media print {
  body {
    background: #fff;
    padding: 0;
  }
  .no-print {
    display: none !important;
  }
  @page {
    size: A4;
    margin: 14mm;
  }
}`;
